import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../App';
import DashboardLayout from '../components/DashboardLayout';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const PLAN_LIMITS = { free: 1, starter: 1, pro: 3, agency: 5 };

export default function Integrations() {
  const { token, user } = useContext(AuthContext);
  const clientId = localStorage.getItem('connecting_client_id');
  const [metaStatus, setMetaStatus] = useState({ connected: false });
  const [googleStatus, setGoogleStatus] = useState({ connected: false });
  const [loading, setLoading] = useState(true);
  const [showWizard, setShowWizard] = useState(false);
  const [step, setStep] = useState(1);
  const [businesses, setBusinesses] = useState([]);
  const [selectedBusiness, setSelectedBusiness] = useState(null);
  const [adAccounts, setAdAccounts] = useState([]);
  const [selectedAccounts, setSelectedAccounts] = useState([]);
  const [syncing, setSyncing] = useState(false);
  const [showGoogleWizard, setShowGoogleWizard] = useState(false);
  const [googleAccounts, setGoogleAccounts] = useState([]);
  const [selectedGoogleAccounts, setSelectedGoogleAccounts] = useState([]);
  const [googleSyncing, setGoogleSyncing] = useState(false);
  const [ga4Status, setGa4Status] = useState({ connected: false });
  const [gscStatus, setGscStatus] = useState({ connected: false });
  const [showGa4Wizard, setShowGa4Wizard] = useState(false);
  const [showGscWizard, setShowGscWizard] = useState(false);
  const [ga4Properties, setGa4Properties] = useState([]);
  const [gscSites, setGscSites] = useState([]);
  const [ga4Saving, setGa4Saving] = useState(false);
  const [gscSaving, setGscSaving] = useState(false);
  const [googleSyncDone, setGoogleSyncDone] = useState(false);
  const [syncDone, setSyncDone] = useState(false);

  const plan = user?.subscription_plan || 'free';
  const maxAccounts = PLAN_LIMITS[plan] || 1;
  const headers = { Authorization: `Bearer ${token}` };

  const checkMetaStatus = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/meta/status`, { headers });
      const data = await res.json();
      setMetaStatus(data);
      if (data.connected && new URLSearchParams(window.location.search).get('meta') === 'success' && !syncDone) {
        window.history.replaceState({}, document.title, '/integrations');
        startWizard();
      }
      if (data.connected && clientId) {
        // Save client_id to meta_connection
        fetch(`${BACKEND_URL}/api/meta/assign-client`, {
          method: 'POST',
          headers: { ...{ Authorization: `Bearer ${token}` }, 'Content-Type': 'application/json' },
          body: JSON.stringify({ client_id: clientId })
        }).catch(() => {});
      }
    } catch (e) { setMetaStatus({ connected: false }); }
    finally { setLoading(false); }
  };

  const checkGoogleStatus = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/google/status`, { headers });
      const data = await res.json();
      setGoogleStatus(data);
    } catch (e) { setGoogleStatus({ connected: false }); }
  };

  useEffect(() => { checkMetaStatus(); checkGoogleStatus(); checkGa4Status(); checkGscStatus(); }, []);

  const handleConnectMeta = async () => {
    const res = await fetch(`${BACKEND_URL}/api/auth/meta/login`, { headers });
    const data = await res.json();
    if (data.url) window.location.href = data.url;
  };

  const handleDisconnectMeta = async () => {
    await fetch(`${BACKEND_URL}/api/meta/disconnect`, { method: 'DELETE', headers });
    setMetaStatus({ connected: false });
  };


  const checkGa4Status = async () => {
    try {
      const res = await fetch(BACKEND_URL + "/api/ga4/status", { headers });
      const data = await res.json();
      setGa4Status(data);
    } catch (e) { setGa4Status({ connected: false }); }
  };

  const checkGscStatus = async () => {
    try {
      const res = await fetch(BACKEND_URL + "/api/gsc/status", { headers });
      const data = await res.json();
      setGscStatus(data);
    } catch (e) { setGscStatus({ connected: false }); }
  };

  const startGa4Wizard = async () => {
    setShowGa4Wizard(true);
    setGa4Properties([]);
    try {
      const res = await fetch(BACKEND_URL + "/api/ga4/properties", { headers });
      const data = await res.json();
      setGa4Properties(data.data || []);
    } catch (e) { setGa4Properties([]); }
  };

  const saveGa4Property = async (prop) => {
    setGa4Saving(true);
    await fetch(BACKEND_URL + "/api/ga4/save-property", {
      method: "POST",
      headers: { ...headers, "Content-Type": "application/json" },
      body: JSON.stringify({ property_id: prop.id, property_name: prop.name, account_id: prop.account_id, account_name: prop.account_name, client_id: clientId || null })
    });
    setGa4Saving(false);
    setShowGa4Wizard(false);
    await checkGa4Status();
  };

  const startGscWizard = async () => {
    setShowGscWizard(true);
    setGscSites([]);
    try {
      const res = await fetch(BACKEND_URL + "/api/gsc/sites", { headers });
      const data = await res.json();
      setGscSites(data.data || []);
    } catch (e) { setGscSites([]); }
  };

  const saveGscSite = async (site) => {
    setGscSaving(true);
    await fetch(BACKEND_URL + "/api/gsc/save-site", {
      method: "POST",
      headers: { ...headers, "Content-Type": "application/json" },
      body: JSON.stringify({ site_url: site.url, site_name: site.name, client_id: clientId || null })
    });
    setGscSaving(false);
    setShowGscWizard(false);
    await checkGscStatus();
  };

  const handleDisconnectGa4 = async () => {
    await fetch(BACKEND_URL + "/api/ga4/disconnect", { method: "DELETE", headers });
    setGa4Status({ connected: false });
  };

  const handleDisconnectGsc = async () => {
    await fetch(BACKEND_URL + "/api/gsc/disconnect", { method: "DELETE", headers });
    setGscStatus({ connected: false });
  };
  const handleConnectGoogle = async () => {
    const res = await fetch(`${BACKEND_URL}/api/auth/google/login`, { headers });
    const data = await res.json();
    if (data.url) window.location.href = data.url;
  };

  const handleDisconnectGoogle = async () => {
    await fetch(`${BACKEND_URL}/api/google/disconnect`, { method: 'DELETE', headers });
    setGoogleStatus({ connected: false });
  };

  const startGoogleWizard = async () => {
    setShowGoogleWizard(true);
    setGoogleSyncDone(false);
    setSelectedGoogleAccounts([]);
    try {
      const res = await fetch(`${BACKEND_URL}/api/google/customer-accounts`, { headers });
      const data = await res.json();
      setGoogleAccounts(data.data || []);
    } catch (e) {
      setGoogleAccounts([]);
    }
  };

  const toggleGoogleAccount = (accountId) => {
    setSelectedGoogleAccounts(prev => {
      if (prev.includes(accountId)) return prev.filter(a => a !== accountId);
      if (prev.length >= maxAccounts) return prev;
      return [...prev, accountId];
    });
  };

  const saveAndSyncGoogle = async () => {
    setGoogleSyncing(true);
    try {
      await fetch(`${BACKEND_URL}/api/google/save-accounts`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ ad_account_ids: selectedGoogleAccounts })
      });
    } catch (e) {}
    setGoogleSyncing(false);
    setGoogleSyncDone(true);
    await checkGoogleStatus();
  };

  const startWizard = async () => {
    setShowWizard(true); setStep(1); setSyncDone(false);
    setSelectedAccounts([]); setSelectedBusiness(null);
    const res = await fetch(`${BACKEND_URL}/api/meta/business-managers`, { headers });
    const data = await res.json();
    const biz = data.data || [];
    setBusinesses(biz);
    if (biz.length === 0) {
      const accRes = await fetch(`${BACKEND_URL}/api/meta/personal-ad-accounts`, { headers });
      const accData = await accRes.json();
      setAdAccounts(accData.data || []);
      setStep(2);
    }
  };

  const selectBusiness = async (biz) => {
    setSelectedBusiness(biz);
    const res = await fetch(`${BACKEND_URL}/api/meta/ad-accounts-by-business/${biz.id}`, { headers });
    const data = await res.json();
    setAdAccounts(data.data || []);
    setStep(2);
  };

  const toggleAccount = (accountId) => {
    setSelectedAccounts(prev => {
      if (prev.includes(accountId)) return prev.filter(a => a !== accountId);
      if (prev.length >= maxAccounts) return prev;
      return [...prev, accountId];
    });
  };

  const saveAndSync = async () => {
    setSyncing(true); setStep(3);
    await fetch(`${BACKEND_URL}/api/meta/save-accounts?business_manager_id=${selectedBusiness?.id || ''}&business_manager_name=${selectedBusiness?.name || 'Personal'}`, {
      method: 'POST',
      headers: { ...headers, 'Content-Type': 'application/json' },
      body: JSON.stringify({ ad_account_ids: selectedAccounts })
    });
    setSyncing(false); setSyncDone(true);
    await checkMetaStatus();
  };

  return (
    <DashboardLayout>
    <div style={{ padding: '40px', maxWidth: '860px', margin: '0 auto', fontFamily: 'Inter, sans-serif' }}>
      <h1 style={{ fontSize: '28px', fontWeight: '700', marginBottom: '8px', color: '#0f172a' }}>Integraciones</h1>
      <p style={{ color: '#64748b', marginBottom: '32px' }}>Conecta tus plataformas de marketing para sincronizar datos automaticamente</p>

      {/* META ADS CARD */}
      <div style={{ background: 'white', borderRadius: '16px', padding: '32px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', border: '1px solid #e2e8f0', marginBottom: '16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '20px' }}>
          <div style={{ width: '56px', height: '56px', background: '#eff6ff', borderRadius: '14px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '26px' }}>📘</div>
          <div style={{ flex: 1 }}>
            <h2 style={{ fontSize: '18px', fontWeight: '700', margin: 0, color: '#0f172a' }}>Meta Ads</h2>
            <p style={{ color: '#64748b', margin: '2px 0 0', fontSize: '14px' }}>Facebook e Instagram Ads</p>
          </div>
          {metaStatus.connected && <span style={{ background: '#dcfce7', color: '#16a34a', padding: '5px 14px', borderRadius: '20px', fontSize: '13px', fontWeight: '600' }}>✓ Conectado</span>}
        </div>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '24px' }}>
          {['Gasto', 'Impresiones', 'Clicks', 'CTR', 'CPC', 'ROAS', 'Conversiones'].map(tag => (
            <span key={tag} style={{ background: '#eff6ff', color: '#2563eb', padding: '4px 12px', borderRadius: '20px', fontSize: '13px', fontWeight: '500' }}>{tag}</span>
          ))}
        </div>
        {metaStatus.connected ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px' }}>
            <p style={{ margin: 0, color: '#475569', fontSize: '14px' }}>Conectado como <strong>{metaStatus.meta_user_name}</strong></p>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button onClick={startWizard} style={{ background: '#eff6ff', color: '#2563eb', border: 'none', borderRadius: '10px', padding: '10px 20px', cursor: 'pointer', fontWeight: '600', fontSize: '14px' }}>Configurar cuentas</button>
              <button onClick={handleDisconnectMeta} style={{ background: '#fff1f2', color: '#e11d48', border: 'none', borderRadius: '10px', padding: '10px 20px', cursor: 'pointer', fontWeight: '600', fontSize: '14px' }}>Desconectar</button>
            </div>
          </div>
        ) : (
          <button onClick={handleConnectMeta} disabled={loading} style={{ width: '100%', background: '#2563eb', color: 'white', border: 'none', borderRadius: '12px', padding: '16px', cursor: 'pointer', fontSize: '16px', fontWeight: '600' }}>
            Conectar con Facebook
          </button>
        )}
      </div>

      {/* GOOGLE ADS CARD */}
      <div style={{ background: 'white', borderRadius: '16px', padding: '32px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', border: '1px solid #e2e8f0', marginBottom: '16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '20px' }}>
          <div style={{ width: '56px', height: '56px', background: '#fff7ed', borderRadius: '14px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '26px' }}>🎯</div>
          <div style={{ flex: 1 }}>
            <h2 style={{ fontSize: '18px', fontWeight: '700', margin: 0, color: '#0f172a' }}>Google Ads</h2>
            <p style={{ color: '#64748b', margin: '2px 0 0', fontSize: '14px' }}>Search, Display y YouTube Ads</p>
          </div>
          {googleStatus.connected && <span style={{ background: '#dcfce7', color: '#16a34a', padding: '5px 14px', borderRadius: '20px', fontSize: '13px', fontWeight: '600' }}>✓ Conectado</span>}
        </div>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '24px' }}>
          {['Gasto', 'Impresiones', 'Clicks', 'CTR', 'CPC', 'Conversiones'].map(tag => (
            <span key={tag} style={{ background: '#fff7ed', color: '#ea580c', padding: '4px 12px', borderRadius: '20px', fontSize: '13px', fontWeight: '500' }}>{tag}</span>
          ))}
        </div>
        {googleStatus.connected ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px' }}>
            <p style={{ margin: 0, color: '#475569', fontSize: '14px' }}>Conectado como <strong>{googleStatus.google_user_email}</strong></p>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button onClick={startGoogleWizard} style={{ background: '#fff7ed', color: '#ea580c', border: 'none', borderRadius: '10px', padding: '10px 20px', cursor: 'pointer', fontWeight: '600', fontSize: '14px' }}>Configurar cuentas</button>
              <button onClick={handleDisconnectGoogle} style={{ background: '#fff1f2', color: '#e11d48', border: 'none', borderRadius: '10px', padding: '10px 20px', cursor: 'pointer', fontWeight: '600', fontSize: '14px' }}>Desconectar</button>
            </div>
          </div>
        ) : (
          <button onClick={handleConnectGoogle} style={{ width: '100%', background: '#ea580c', color: 'white', border: 'none', borderRadius: '12px', padding: '16px', cursor: 'pointer', fontSize: '16px', fontWeight: '600' }}>
            Conectar con Google Ads
          </button>
        )}
      </div>

      {/* WIZARD MODAL META */}
      {showWizard && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px' }}>
          <div style={{ background: 'white', borderRadius: '20px', width: '100%', maxWidth: '540px', padding: '32px', maxHeight: '80vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', gap: '8px', marginBottom: '28px' }}>
              {['Business Manager', 'Ad Accounts', 'Sincronizando'].map((s, i) => (
                <div key={i} style={{ flex: 1, height: '4px', borderRadius: '4px', background: step > i ? '#2563eb' : '#e2e8f0' }} />
              ))}
            </div>
            {step === 1 && (
              <div>
                <h3 style={{ fontSize: '20px', fontWeight: '700', marginBottom: '8px' }}>Selecciona tu Business Manager</h3>
                <p style={{ color: '#64748b', fontSize: '14px', marginBottom: '20px' }}>Elige el negocio que contiene las cuentas publicitarias</p>
                {businesses.length === 0 ? <div style={{ textAlign: 'center', padding: '40px', color: '#94a3b8' }}>Cargando...</div> : businesses.map(biz => (
                  <div key={biz.id} onClick={() => selectBusiness(biz)}
                    style={{ padding: '16px', border: '2px solid #e2e8f0', borderRadius: '12px', marginBottom: '10px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '12px' }}
                    onMouseEnter={e => e.currentTarget.style.borderColor = '#2563eb'}
                    onMouseLeave={e => e.currentTarget.style.borderColor = '#e2e8f0'}>
                    <div style={{ width: '40px', height: '40px', background: '#eff6ff', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '20px' }}>🏢</div>
                    <div><p style={{ margin: 0, fontWeight: '600' }}>{biz.name}</p><p style={{ margin: 0, fontSize: '13px', color: '#94a3b8' }}>ID: {biz.id}</p></div>
                    <span style={{ marginLeft: 'auto', color: '#2563eb' }}>→</span>
                  </div>
                ))}
                <button onClick={() => setShowWizard(false)} style={{ width: '100%', marginTop: '16px', background: 'none', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '12px', cursor: 'pointer', color: '#64748b' }}>Cancelar</button>
              </div>
            )}
            {step === 2 && (
              <div>
                <h3 style={{ fontSize: '20px', fontWeight: '700', marginBottom: '8px' }}>Selecciona las cuentas publicitarias</h3>
                <p style={{ color: '#64748b', fontSize: '14px', marginBottom: '4px' }}>Hasta <strong>{maxAccounts}</strong> cuenta{maxAccounts > 1 ? 's' : ''} con tu plan <strong style={{ textTransform: 'capitalize' }}>{plan}</strong></p>
                <p style={{ color: '#94a3b8', fontSize: '13px', marginBottom: '20px' }}>{selectedAccounts.length}/{maxAccounts} seleccionadas</p>
                {adAccounts.map(acc => {
                  const isSelected = selectedAccounts.includes(acc.id);
                  const isDisabled = !isSelected && selectedAccounts.length >= maxAccounts;
                  return (
                    <div key={acc.id} onClick={() => !isDisabled && toggleAccount(acc.id)}
                      style={{ padding: '16px', border: `2px solid ${isSelected ? '#2563eb' : '#e2e8f0'}`, borderRadius: '12px', marginBottom: '10px', cursor: isDisabled ? 'not-allowed' : 'pointer', background: isSelected ? '#eff6ff' : 'white', opacity: isDisabled ? 0.5 : 1, display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <div style={{ width: '22px', height: '22px', borderRadius: '6px', border: `2px solid ${isSelected ? '#2563eb' : '#cbd5e1'}`, background: isSelected ? '#2563eb' : 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        {isSelected && <span style={{ color: 'white', fontSize: '14px' }}>✓</span>}
                      </div>
                      <div style={{ flex: 1 }}>
                        <p style={{ margin: 0, fontWeight: '600', fontSize: '15px' }}>{acc.name}</p>
                        <p style={{ margin: '2px 0 0', fontSize: '13px', color: '#94a3b8' }}>ID: {acc.id} • {acc.currency}</p>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <p style={{ margin: 0, fontSize: '13px', fontWeight: '600', color: '#2563eb' }}>${parseFloat(acc.amount_spent || 0).toLocaleString()}</p>
                        <p style={{ margin: 0, fontSize: '12px', color: '#94a3b8' }}>gastado</p>
                      </div>
                    </div>
                  );
                })}
                <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
                  {businesses.length > 0 && <button onClick={() => setStep(1)} style={{ flex: 1, background: 'none', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '12px', cursor: 'pointer', color: '#64748b' }}>← Volver</button>}
                  <button onClick={saveAndSync} disabled={selectedAccounts.length === 0}
                    style={{ flex: 2, background: selectedAccounts.length > 0 ? '#2563eb' : '#e2e8f0', color: selectedAccounts.length > 0 ? 'white' : '#94a3b8', border: 'none', borderRadius: '10px', padding: '14px', cursor: selectedAccounts.length > 0 ? 'pointer' : 'not-allowed', fontWeight: '600', fontSize: '15px' }}>
                    Sincronizar {selectedAccounts.length > 0 ? `${selectedAccounts.length} cuenta${selectedAccounts.length > 1 ? 's' : ''}` : ''}
                  </button>
                </div>
              </div>
            )}
            {step === 3 && (
              <div style={{ textAlign: 'center', padding: '20px 0' }}>
                {syncing ? (
                  <><div style={{ fontSize: '48px', marginBottom: '16px' }}>⚡</div><h3 style={{ fontSize: '20px', fontWeight: '700', marginBottom: '8px' }}>Sincronizando...</h3><p style={{ color: '#64748b' }}>Importando campañas de los últimos 30 días</p></>
                ) : (
                  <><div style={{ fontSize: '48px', marginBottom: '16px' }}>🎉</div><h3 style={{ fontSize: '20px', fontWeight: '700', marginBottom: '8px' }}>¡Listo! Datos sincronizados</h3><p style={{ color: '#64748b', marginBottom: '24px' }}>Tus campañas ya están disponibles en el dashboard</p>
                  <button onClick={() => { setShowWizard(false); window.location.href = '/dashboard'; }} style={{ background: '#2563eb', color: 'white', border: 'none', borderRadius: '12px', padding: '14px 32px', cursor: 'pointer', fontWeight: '600', fontSize: '15px' }}>Ver Dashboard →</button></>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* GA4 CARD */}
      <div style={{ background: 'white', borderRadius: '16px', padding: '32px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', border: '1px solid #e2e8f0', marginBottom: '16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '20px' }}>
          <div style={{ width: '56px', height: '56px', background: '#fff8e1', borderRadius: '14px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '26px' }}>📊</div>
          <div style={{ flex: 1 }}>
            <h2 style={{ fontSize: '18px', fontWeight: '700', margin: 0, color: '#0f172a' }}>Google Analytics 4</h2>
            <p style={{ color: '#64748b', margin: '2px 0 0', fontSize: '14px' }}>Sesiones, usuarios, conversiones y comportamiento web</p>
          </div>
          {ga4Status.connected && <span style={{ background: '#dcfce7', color: '#16a34a', padding: '5px 14px', borderRadius: '20px', fontSize: '13px', fontWeight: '600' }}>Conectado</span>}
        </div>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '24px' }}>
          {['Sesiones', 'Usuarios', 'Pageviews', 'Tasa Rebote', 'Conversiones', 'Ingresos'].map(tag => (
            <span key={tag} style={{ background: '#fff8e1', color: '#f59e0b', padding: '4px 12px', borderRadius: '20px', fontSize: '13px', fontWeight: '500' }}>{tag}</span>
          ))}
        </div>
        {ga4Status.connected ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px' }}>
            <p style={{ margin: 0, color: '#475569', fontSize: '14px' }}>Propiedad: <strong>{ga4Status.property_name}</strong></p>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button onClick={startGa4Wizard} style={{ background: '#fff8e1', color: '#f59e0b', border: 'none', borderRadius: '10px', padding: '10px 20px', cursor: 'pointer', fontWeight: '600', fontSize: '14px' }}>Cambiar propiedad</button>
              <button onClick={handleDisconnectGa4} style={{ background: '#fff1f2', color: '#e11d48', border: 'none', borderRadius: '10px', padding: '10px 20px', cursor: 'pointer', fontWeight: '600', fontSize: '14px' }}>Desconectar</button>
            </div>
          </div>
        ) : (
          <button onClick={startGa4Wizard} style={{ width: '100%', background: googleStatus.connected ? '#f59e0b' : '#e2e8f0', color: googleStatus.connected ? 'white' : '#94a3b8', border: 'none', borderRadius: '12px', padding: '16px', cursor: 'pointer', fontSize: '16px', fontWeight: '600' }}>
            {googleStatus.connected ? 'Conectar Google Analytics 4' : 'Primero conecta Google Ads'}
          </button>
        )}
      </div>

      {/* GSC CARD */}
      <div style={{ background: 'white', borderRadius: '16px', padding: '32px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', border: '1px solid #e2e8f0', marginBottom: '16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '20px' }}>
          <div style={{ width: '56px', height: '56px', background: '#f0fdf4', borderRadius: '14px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '26px' }}>🔍</div>
          <div style={{ flex: 1 }}>
            <h2 style={{ fontSize: '18px', fontWeight: '700', margin: 0, color: '#0f172a' }}>Google Search Console</h2>
            <p style={{ color: '#64748b', margin: '2px 0 0', fontSize: '14px' }}>Impresiones, clicks, posicion promedio y palabras clave</p>
          </div>
          {gscStatus.connected && <span style={{ background: '#dcfce7', color: '#16a34a', padding: '5px 14px', borderRadius: '20px', fontSize: '13px', fontWeight: '600' }}>Conectado</span>}
        </div>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '24px' }}>
          {['Impresiones', 'Clicks', 'CTR', 'Posicion', 'Keywords', 'Paginas'].map(tag => (
            <span key={tag} style={{ background: '#f0fdf4', color: '#16a34a', padding: '4px 12px', borderRadius: '20px', fontSize: '13px', fontWeight: '500' }}>{tag}</span>
          ))}
        </div>
        {gscStatus.connected ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px' }}>
            <p style={{ margin: 0, color: '#475569', fontSize: '14px' }}>Sitio: <strong>{gscStatus.site_url}</strong></p>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button onClick={startGscWizard} style={{ background: '#f0fdf4', color: '#16a34a', border: 'none', borderRadius: '10px', padding: '10px 20px', cursor: 'pointer', fontWeight: '600', fontSize: '14px' }}>Cambiar sitio</button>
              <button onClick={handleDisconnectGsc} style={{ background: '#fff1f2', color: '#e11d48', border: 'none', borderRadius: '10px', padding: '10px 20px', cursor: 'pointer', fontWeight: '600', fontSize: '14px' }}>Desconectar</button>
            </div>
          </div>
        ) : (
          <button onClick={startGscWizard} style={{ width: '100%', background: googleStatus.connected ? '#16a34a' : '#e2e8f0', color: googleStatus.connected ? 'white' : '#94a3b8', border: 'none', borderRadius: '12px', padding: '16px', cursor: 'pointer', fontSize: '16px', fontWeight: '600' }}>
            {googleStatus.connected ? 'Conectar Search Console' : 'Primero conecta Google Ads'}
          </button>
        )}
      </div>
      {/* WIZARD GOOGLE */}
      {/* WIZARD GA4 */}
      {showGa4Wizard && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px' }}>
          <div style={{ background: 'white', borderRadius: '20px', width: '100%', maxWidth: '520px', padding: '32px', maxHeight: '80vh', overflowY: 'auto' }}>
            <h3 style={{ fontSize: '20px', fontWeight: '700', marginBottom: '8px' }}>Selecciona tu propiedad GA4</h3>
            <p style={{ color: '#64748b', fontSize: '14px', marginBottom: '20px' }}>Elige la propiedad de Google Analytics 4</p>
            {ga4Properties.length === 0 ? <div style={{ textAlign: 'center', padding: '40px', color: '#94a3b8' }}>Cargando propiedades...</div> : ga4Properties.map(prop => (
              <div key={prop.id} onClick={() => saveGa4Property(prop)} style={{ padding: '16px', border: '2px solid #e2e8f0', borderRadius: '12px', marginBottom: '10px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{ width: '40px', height: '40px', background: '#fff8e1', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '20px' }}>📊</div>
                <div><p style={{ margin: 0, fontWeight: '600' }}>{prop.name}</p><p style={{ margin: 0, fontSize: '13px', color: '#94a3b8' }}>{prop.account_name} • ID: {prop.id}</p></div>
                <span style={{ marginLeft: 'auto', color: '#f59e0b' }}>→</span>
              </div>
            ))}
            <button onClick={() => setShowGa4Wizard(false)} style={{ width: '100%', marginTop: '16px', background: 'none', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '12px', cursor: 'pointer', color: '#64748b' }}>Cancelar</button>
          </div>
        </div>
      )}

      {/* WIZARD GSC */}
      {showGscWizard && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px' }}>
          <div style={{ background: 'white', borderRadius: '20px', width: '100%', maxWidth: '520px', padding: '32px', maxHeight: '80vh', overflowY: 'auto' }}>
            <h3 style={{ fontSize: '20px', fontWeight: '700', marginBottom: '8px' }}>Selecciona tu sitio web</h3>
            <p style={{ color: '#64748b', fontSize: '14px', marginBottom: '20px' }}>Elige el sitio de Google Search Console</p>
            {gscSites.length === 0 ? <div style={{ textAlign: 'center', padding: '40px', color: '#94a3b8' }}>Cargando sitios...</div> : gscSites.map(site => (
              <div key={site.url} onClick={() => saveGscSite(site)} style={{ padding: '16px', border: '2px solid #e2e8f0', borderRadius: '12px', marginBottom: '10px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{ width: '40px', height: '40px', background: '#f0fdf4', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '20px' }}>🔍</div>
                <div><p style={{ margin: 0, fontWeight: '600' }}>{site.name}</p><p style={{ margin: 0, fontSize: '13px', color: '#94a3b8' }}>{site.url}</p></div>
                <span style={{ marginLeft: 'auto', color: '#16a34a' }}>→</span>
              </div>
            ))}
            <button onClick={() => setShowGscWizard(false)} style={{ width: '100%', marginTop: '16px', background: 'none', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '12px', cursor: 'pointer', color: '#64748b' }}>Cancelar</button>
          </div>
        </div>
      )}

      {showGoogleWizard && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px' }}>
          <div style={{ background: 'white', borderRadius: '20px', width: '100%', maxWidth: '540px', padding: '32px', maxHeight: '80vh', overflowY: 'auto' }}>
            {!googleSyncDone ? (
              <div>
                <h3 style={{ fontSize: '20px', fontWeight: '700', marginBottom: '8px' }}>Selecciona tus cuentas de Google Ads</h3>
                <p style={{ color: '#64748b', fontSize: '14px', marginBottom: '4px' }}>Hasta <strong>{maxAccounts}</strong> cuenta{maxAccounts > 1 ? 's' : ''} con tu plan <strong style={{ textTransform: 'capitalize' }}>{plan}</strong></p>
                <p style={{ color: '#94a3b8', fontSize: '13px', marginBottom: '20px' }}>{selectedGoogleAccounts.length}/{maxAccounts} seleccionadas</p>
                {googleAccounts.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '40px', color: '#94a3b8' }}>Cargando cuentas...</div>
                ) : (
                  googleAccounts.map(acc => {
                    const isSelected = selectedGoogleAccounts.includes(acc.id);
                    const isDisabled = !isSelected && selectedGoogleAccounts.length >= maxAccounts;
                    return (
                      <div key={acc.id} onClick={() => !isDisabled && toggleGoogleAccount(acc.id)}
                        style={{ padding: '16px', border: `2px solid ${isSelected ? '#ea580c' : '#e2e8f0'}`, borderRadius: '12px', marginBottom: '10px', cursor: isDisabled ? 'not-allowed' : 'pointer', background: isSelected ? '#fff7ed' : 'white', opacity: isDisabled ? 0.5 : 1, display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <div style={{ width: '22px', height: '22px', borderRadius: '6px', border: `2px solid ${isSelected ? '#ea580c' : '#cbd5e1'}`, background: isSelected ? '#ea580c' : 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                          {isSelected && <span style={{ color: 'white', fontSize: '14px' }}>✓</span>}
                        </div>
                        <div style={{ flex: 1 }}>
                          <p style={{ margin: 0, fontWeight: '600', fontSize: '15px' }}>🎯 {acc.name}</p>
                          <p style={{ margin: '2px 0 0', fontSize: '13px', color: '#94a3b8' }}>ID: {acc.id.replace('customers/', '')}</p>
                        </div>
                      </div>
                    );
                  })
                )}
                <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
                  <button onClick={() => setShowGoogleWizard(false)} style={{ flex: 1, background: 'none', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '12px', cursor: 'pointer', color: '#64748b' }}>Cancelar</button>
                  <button onClick={saveAndSyncGoogle} disabled={selectedGoogleAccounts.length === 0 || googleSyncing}
                    style={{ flex: 2, background: selectedGoogleAccounts.length > 0 ? '#ea580c' : '#e2e8f0', color: selectedGoogleAccounts.length > 0 ? 'white' : '#94a3b8', border: 'none', borderRadius: '10px', padding: '14px', cursor: selectedGoogleAccounts.length > 0 ? 'pointer' : 'not-allowed', fontWeight: '600', fontSize: '15px' }}>
                    {googleSyncing ? 'Sincronizando...' : `Sincronizar ${selectedGoogleAccounts.length > 0 ? selectedGoogleAccounts.length + ' cuenta' + (selectedGoogleAccounts.length > 1 ? 's' : '') : ''}`}
                  </button>
                </div>
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: '20px 0' }}>
                <div style={{ fontSize: '48px', marginBottom: '16px' }}>🎉</div>
                <h3 style={{ fontSize: '20px', fontWeight: '700', marginBottom: '8px' }}>¡Google Ads sincronizado!</h3>
                <p style={{ color: '#64748b', marginBottom: '24px' }}>Tus campañas de Google Ads ya están disponibles</p>
                <button onClick={() => { setShowGoogleWizard(false); window.location.href = '/dashboard'; }}
                  style={{ background: '#ea580c', color: 'white', border: 'none', borderRadius: '12px', padding: '14px 32px', cursor: 'pointer', fontWeight: '600', fontSize: '15px' }}>
                  Ver Dashboard →
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
    </DashboardLayout>
  );
}
